import { Link, useNavigate } from 'react-router-dom';
import { FileText } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell,
} from 'recharts';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { formatNumber, formatCurrency, formatDate, getStatusBadgeClass, getStatusLabel } from '@/lib/utils';
import type { RecentRequirement, RecentActual, DashboardQueueItem } from '@/lib/types';
import {
  CHART_COLORS, STATUS_LABELS, LABOUR_TYPE_LABELS, SKILL_LEVEL_LABELS,
  safeNum, compactINR, adaptiveChartHeight, SectionEmpty, SectionHeader, ClassificationCallout,
} from './shared';

export interface DashboardUrls {
  reqUrl: (opts?: Record<string, string>) => string;
  actualsUrl: (opts?: Record<string, string>) => string;
  /** Raw analysis filter bar values so metric drill-downs can mirror them. */
  filters?: { projectId: string; startDate: string; endDate: string };
}

interface PendingRowProps {
  number: string;
  date?: string;
  projectName?: string;
  status: string;
  to: string;
  cost?: number;
}

function PendingRow({ number, date, projectName, status, to, cost }: PendingRowProps) {
  return (
    <Link
      to={to}
      className="flex items-center justify-between p-3 rounded-lg hover:bg-surface-hover border border-gray-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
    >
      <div className="flex items-center gap-3 min-w-0">
        <FileText className="text-muted flex-shrink-0" size={18} />
        <div className="min-w-0">
          <p className="text-sm font-medium text-primary truncate">{number}</p>
          <p className="text-xs text-secondary truncate">{date ? formatDate(date) : ''}{projectName ? ` · ${projectName}` : ''}</p>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {cost !== undefined && <span className="text-sm font-semibold text-primary">{formatCurrency(safeNum(cost))}</span>}
        <Badge variant={getStatusBadgeClass(status) as any}>{getStatusLabel(status)}</Badge>
      </div>
    </Link>
  );
}

function requirementRow(r: RecentRequirement) {
  return (
    <PendingRow
      key={r.id}
      number={r.requirementNumber}
      date={r.startDate}
      projectName={r.projectName}
      status={r.status}
      to={`/requirements/${r.id}`}
    />
  );
}

function actualRow(a: RecentActual) {
  return (
    <PendingRow
      key={a.id}
      number={a.actualNumber}
      date={a.workDate}
      projectName={a.projectName}
      status={a.status}
      to={`/actuals/${a.id}`}
      cost={a.totalCost}
    />
  );
}

interface ListCardProps {
  title: string;
  emptyText: string;
  items: RecentRequirement[];
  linkTo?: string;
  linkLabel?: string;
  count?: number;
  countLabel?: string;
}

export function RequirementListCard({ title, emptyText, items, linkTo, linkLabel, count, countLabel }: ListCardProps) {
  return (
    <Card>
      <SectionHeader title={title} link={linkTo && linkLabel ? { to: linkTo, label: linkLabel } : undefined} />
      <CardContent>
        {count !== undefined && count > 0 && (
          <p className="mb-2 text-sm text-secondary">
            <span className="font-semibold text-primary">{formatNumber(count)}</span> {countLabel || 'pending'}
          </p>
        )}
        {items && items.length > 0 ? (
          <div className="space-y-2">{items.map(requirementRow)}</div>
        ) : (
          <SectionEmpty message={emptyText} />
        )}
      </CardContent>
    </Card>
  );
}

export function ActualListCard({ title, emptyText, items, linkTo, linkLabel, count, countLabel }: ListCardProps & { items: RecentActual[] }) {
  return (
    <Card>
      <SectionHeader title={title} link={linkTo && linkLabel ? { to: linkTo, label: linkLabel } : undefined} />
      <CardContent>
        {count !== undefined && count > 0 && (
          <p className="mb-2 text-sm text-secondary">
            <span className="font-semibold text-primary">{formatNumber(count)}</span> {countLabel || 'pending'}
          </p>
        )}
        {items && items.length > 0 ? (
          <div className="space-y-2">{items.map(actualRow)}</div>
        ) : (
          <SectionEmpty message={emptyText} />
        )}
      </CardContent>
    </Card>
  );
}

// ── Recent section (read-only roles) ───────────────────────────────────────

export interface QueueRowDisplay {
  date?: string;
  action?: string;
  meta?: string;
}

function itemDetailPath(type: DashboardQueueItem['type'], id: string): string {
  return type === 'actual' ? `/actuals/${id}` : `/requirements/${id}`;
}

/** Display rules for compact recent sections (last-touched date only). */
export function recentDisplay(item: DashboardQueueItem): QueueRowDisplay {
  return { date: item.updatedAt };
}

export function DashboardItemRow({ item, date, action, meta }: { item: DashboardQueueItem } & QueueRowDisplay) {
  return (
    <Link
      to={itemDetailPath(item.type, item.id)}
      className="flex items-center justify-between gap-3 p-3 rounded-lg hover:bg-surface-hover border border-gray-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
    >
      <div className="flex items-center gap-3 min-w-0">
        <FileText className="text-muted flex-shrink-0" size={18} />
        <div className="min-w-0">
          <p className="text-sm font-medium text-primary truncate">{item.number}</p>
          <p className="text-xs text-secondary truncate">{date ? formatDate(date) : ''}{item.projectName ? ` · ${item.projectName}` : ''}</p>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {action && <Badge variant="default">{action}</Badge>}
        {meta && <span className="text-xs text-secondary whitespace-nowrap">{meta}</span>}
        <Badge variant={getStatusBadgeClass(item.status) as any}>{getStatusLabel(item.status)}</Badge>
      </div>
    </Link>
  );
}

export interface RecentListCardProps {
  title: string;
  emptyText: string;
  items: DashboardQueueItem[];
  linkTo?: string;
  linkLabel?: string;
}

/** Compact recent-activity section; hidden entirely when it has no rows. */
export function RecentListCard({ title, emptyText, items, linkTo, linkLabel }: RecentListCardProps) {
  return (
    <Card>
      <SectionHeader title={title} link={linkTo && linkLabel ? { to: linkTo, label: linkLabel } : undefined} />
      <CardContent>
        {items && items.length > 0 ? (
          <div className="space-y-2">{items.map((item) => (
            <DashboardItemRow key={`${item.type}-${item.id}`} item={item} {...recentDisplay(item)} />
          ))}</div>
        ) : (
          <SectionEmpty message={emptyText} />
        )}
      </CardContent>
    </Card>
  );
}

export interface StatusCountItem {
  status: string;
  count: number;
}

export function RequirementsByStatusCard({ items, urls }: { items: StatusCountItem[]; urls: DashboardUrls }) {
  const navigate = useNavigate();
  const data = (items || []).map((r) => ({ ...r, label: STATUS_LABELS[r.status] || r.status }));
  const drill = (entry: any) => {
    if (entry?.status) navigate(urls.reqUrl({ status: entry.status }));
  };
  return (
    <Card>
      <SectionHeader title="Requirements by Status" link={{ to: urls.reqUrl(), label: 'View all requirements' }} />
      <CardContent>
        {data.length > 0 ? (
          <div role="img" aria-label={`Requirements by status. ${data.map((s) => `${s.label}: ${formatNumber(safeNum(s.count))}`).join(', ')}`}>
            <ResponsiveContainer width="100%" height={adaptiveChartHeight(data.length)}>
              <BarChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} />
                <Tooltip formatter={(v: number) => formatNumber(safeNum(v))} />
                <Bar dataKey="count" name="Requirements" fill="#3b5998" radius={[4, 4, 0, 0]} cursor="pointer" onClick={drill} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <SectionEmpty message="No requirements in this range." />
        )}
      </CardContent>
    </Card>
  );
}

export interface PlannedVsActualData {
  byProject: { projectId: string; projectName: string; plannedHeadcount: number; actualHeadcount: number }[];
  totals: { plannedHeadcount: number; actualHeadcount: number };
}

export function PlannedVsActualCard({ data, urls }: { data: PlannedVsActualData; urls: DashboardUrls }) {
  const navigate = useNavigate();
  const chartData = (data?.byProject || []).map((p) => ({ ...p, name: p.projectName }));
  const plannedDrill = (entry: any) => {
    if (entry?.projectId) navigate(urls.reqUrl({ status: 'APPROVED', projectId: entry.projectId }));
  };
  const actualDrill = (entry: any) => {
    if (entry?.projectId) navigate(urls.actualsUrl({ status: 'APPROVED', projectId: entry.projectId }));
  };
  return (
    <Card>
      <SectionHeader title="Planned vs Actual Labour" link={{ to: urls.reqUrl({ status: 'APPROVED' }), label: 'View approved requirements' }} />
      <CardContent>
        {chartData.length > 0 ? (
          <>
            <div className="mb-2 text-sm text-secondary">
              Planned headcount <span className="font-semibold text-primary">{formatNumber(safeNum(data.totals.plannedHeadcount))}</span> vs Actual headcount <span className="font-semibold text-primary">{formatNumber(safeNum(data.totals.actualHeadcount))}</span>
            </div>
            <div role="img" aria-label="Planned versus actual headcount by project">
              <ResponsiveContainer width="100%" height={adaptiveChartHeight(chartData.length)}>
                <BarChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} />
                  <Tooltip formatter={(v: number) => formatNumber(safeNum(v))} />
                  <Bar dataKey="plannedHeadcount" name="Planned" fill="#818cf8" radius={[4, 4, 0, 0]} cursor="pointer" onClick={plannedDrill} />
                  <Bar dataKey="actualHeadcount" name="Actual" fill="#10b981" radius={[4, 4, 0, 0]} cursor="pointer" onClick={actualDrill} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </>
        ) : (
          <SectionEmpty message="No planned or actual labour in this range." />
        )}
      </CardContent>
    </Card>
  );
}

export interface LabourByTypeItem {
  labourType: string;
  headcount: number;
}

export function LabourByTypeCard({ items, urls, admin }: { items: LabourByTypeItem[]; urls: DashboardUrls; admin: boolean }) {
  const navigate = useNavigate();
  const typeData = (items || []).map((t) => ({
    ...t,
    name: LABOUR_TYPE_LABELS[t.labourType] || t.labourType,
  }));
  const hasUnclassified = typeData.some((t) => t.labourType === 'UNCLASSIFIED');
  const drill = (entry: any) => {
    const t = entry?.payload?.labourType ?? entry?.labourType;
    if (t) navigate(urls.actualsUrl({ status: 'APPROVED', labourType: t }));
  };
  const aria = `Labour by type. ${typeData.map((t) => `${t.name}: ${formatNumber(safeNum(t.headcount))}`).join(', ')}`;
  return (
    <Card>
      <SectionHeader title="Labour by Type" link={{ to: urls.actualsUrl({ status: 'APPROVED' }), label: 'View actual labour' }} />
      <CardContent>
        {typeData.length > 0 ? (
          <>
            {typeData.length <= 1 ? (
              <div role="img" aria-label={aria} className="space-y-3 py-4">
                {typeData.map((t) => (
                  <button
                    key={t.labourType}
                    type="button"
                    onClick={() => drill({ labourType: t.labourType })}
                    className="w-full flex items-center justify-between rounded-lg border border-gray-100 p-3 text-left hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                  >
                    <span className="text-sm text-secondary">{t.name}</span>
                    <span className="text-sm font-semibold text-primary">{formatNumber(safeNum(t.headcount))} headcount</span>
                  </button>
                ))}
              </div>
            ) : (
              <div role="img" aria-label={aria}>
                <ResponsiveContainer width="100%" height={adaptiveChartHeight(typeData.length, 200)}>
                  <PieChart>
                    <Pie
                      data={typeData}
                      dataKey="headcount"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      innerRadius={45}
                      paddingAngle={2}
                      label={({ name, headcount }: any) => `${name}: ${formatNumber(safeNum(headcount))}`}
                    >
                      {typeData.map((_, i: number) => (
                        <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} cursor="pointer" onClick={drill} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v: number) => formatNumber(safeNum(v))} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
            {hasUnclassified && <ClassificationCallout admin={admin} />}
          </>
        ) : (
          <SectionEmpty message="No actual labour in this range." />
        )}
      </CardContent>
    </Card>
  );
}

export interface LabourBySkillItem {
  skillLevel: string;
  plannedHeadcount: number;
  actualHeadcount: number;
}

export function LabourBySkillCard({ items, urls, admin }: { items: LabourBySkillItem[]; urls: DashboardUrls; admin: boolean }) {
  const navigate = useNavigate();
  const skillData = (items || []).map((s) => ({
    ...s,
    name: SKILL_LEVEL_LABELS[s.skillLevel] || s.skillLevel,
  }));
  const hasUnclassified = skillData.some((s) => s.skillLevel === 'UNCLASSIFIED');
  const plannedDrill = (entry: any) => {
    const s = entry?.payload?.skillLevel ?? entry?.skillLevel;
    if (s) navigate(urls.reqUrl({ status: 'APPROVED', skillLevel: s }));
  };
  const actualDrill = (entry: any) => {
    const s = entry?.payload?.skillLevel ?? entry?.skillLevel;
    if (s) navigate(urls.actualsUrl({ status: 'APPROVED', skillLevel: s }));
  };
  return (
    <Card>
      <SectionHeader title="Labour by Skill Level" link={{ to: urls.actualsUrl({ status: 'APPROVED' }), label: 'View actual labour' }} />
      <CardContent>
        {skillData.length > 0 ? (
          <>
            <div role="img" aria-label={`Labour by skill level. ${skillData.map((s) => `${s.name}: planned ${formatNumber(safeNum(s.plannedHeadcount))}, actual ${formatNumber(safeNum(s.actualHeadcount))}`).join(', ')}`}>
              <ResponsiveContainer width="100%" height={adaptiveChartHeight(skillData.length)}>
                <BarChart data={skillData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} />
                  <Tooltip formatter={(v: number) => formatNumber(safeNum(v))} />
                  <Bar dataKey="plannedHeadcount" name="Planned" fill="#3b5998" radius={[4, 4, 0, 0]} cursor="pointer" onClick={plannedDrill} />
                  <Bar dataKey="actualHeadcount" name="Actual" fill="#f59e0b" radius={[4, 4, 0, 0]} cursor="pointer" onClick={actualDrill} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            {hasUnclassified && <ClassificationCallout admin={admin} />}
          </>
        ) : (
          <SectionEmpty message="No labour data in this range." />
        )}
      </CardContent>
    </Card>
  );
}

export interface CostByProjectItem {
  projectId: string;
  projectName: string;
  totalCost: number;
}

export function CostByProjectCard({ items, urls }: { items: CostByProjectItem[]; urls: DashboardUrls }) {
  const navigate = useNavigate();
  const costData = (items || []).map((p) => ({ ...p, name: p.projectName }));
  const drill = (entry: any) => {
    if (entry?.projectId) navigate(urls.actualsUrl({ status: 'APPROVED', projectId: entry.projectId }));
  };
  return (
    <Card>
      <SectionHeader title="Cost by Project" link={{ to: urls.actualsUrl({ status: 'APPROVED' }), label: 'View actual labour' }} />
      <CardContent>
        {costData.length > 0 ? (
          <div role="img" aria-label={`Cost by project. ${costData.map((c) => `${c.projectName}: ${formatCurrency(safeNum(c.totalCost))}`).join(', ')}`}>
            <ResponsiveContainer width="100%" height={adaptiveChartHeight(costData.length)}>
              <BarChart data={costData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tickFormatter={(v: number) => compactINR(v)} />
                <Tooltip formatter={(v: number) => formatCurrency(safeNum(v))} />
                <Bar dataKey="totalCost" name="Cost" fill="#1e3a5f" radius={[4, 4, 0, 0]} cursor="pointer" onClick={drill} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <SectionEmpty message="No approved actual labour cost in this range." />
        )}
      </CardContent>
    </Card>
  );
}

export interface ApprovalWorkloadData {
  requirements: { pendingManager?: number; pendingCluster?: number };
  actuals: { pendingManager?: number; pendingCluster?: number };
}

export function ApprovalWorkloadCard({ data }: { data: ApprovalWorkloadData }) {
  const rows: { label: string; value?: number }[] = [
    { label: 'Requirements · Pending Manager', value: data?.requirements?.pendingManager },
    { label: 'Requirements · Pending Final', value: data?.requirements?.pendingCluster },
    { label: 'Actuals · Pending Manager', value: data?.actuals?.pendingManager },
    { label: 'Actuals · Pending Final', value: data?.actuals?.pendingCluster },
  ].filter((r) => r.value !== undefined);
  return (
    <Card>
      <SectionHeader title="Approval Workload" link={{ to: '/approvals', label: 'Review approvals' }} />
      <CardContent>
        {rows.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {rows.map((r) => (
              <div key={r.label} className="flex items-center justify-between rounded-lg border border-gray-100 p-3">
                <span className="text-sm text-secondary">{r.label}</span>
                <span className="text-xl font-semibold text-primary">{formatNumber(safeNum(r.value))}</span>
              </div>
            ))}
          </div>
        ) : (
          <SectionEmpty message="Nothing awaiting approval in this range." />
        )}
      </CardContent>
    </Card>
  );
}
